import { FC } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import assets from '@public/assets/pages/interwill/assets.svg';
import electronicVersion from '@public/assets/pages/interwill/electronicVersion.svg';
import signedWillSvg from '@public/assets/pages/interwill/signedWill.svg';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwTextStyled } from '@/_pages/interwill/components/ParticipantContent/styled';
import { IwScenarioSettingsDetails, useIwExecutionStore } from '@/entities/interwills';
import { useDisplayApprovedWill } from '@/entities/interwills/hooks/useDisplayApprovedWill';
import { useDisplayFileStoragePdf } from '@/entities/interwills/hooks/useDisplayFileStoragePdf';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { routes } from '@/shared/helpers';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { Icon } from '@/shared/uikit/Icon';
import { dates } from '@/shared/utils/dates';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AccessInterwillProps } from '@/widgets/modals/types';

import { ModalContainerV5 } from '../containers/ModalContainerV5';

export const AccessInterwillModal: FC<AccessInterwillProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'accessInterwill' });

  const { finalPdfUrl, signedWill, signingDate, createdDate, scenarioSettings } =
    useIwExecutionStore();
  const { displayPdf } = useDisplayApprovedWill(finalPdfUrl);
  const { displayPdf: displaySignedPdf } = useDisplayFileStoragePdf();

  const router = useAppRouter();
  const modals = useModalsStore();

  return (
    <ModalContainerV5 title={t('title')}>
      <InnerForm>
        {signedWill && signingDate && (
          <Item>
            <ImageStyled src={signedWillSvg} width={240} height={152} alt={t('signedDoc')} />

            <ItemContent>
              <ItemTitle>{t('signedDoc')}</ItemTitle>
              <span>{t('signedOn', { date: dates.formatDate(signingDate) })}</span>
              <ItemButton
                onClick={() => {
                  displaySignedPdf(signedWill.url);
                }}
              >
                {t('open')}
              </ItemButton>
            </ItemContent>
          </Item>
        )}

        <Item>
          <ImageStyled
            src={electronicVersion}
            width={240}
            height={152}
            alt={t('electronicVersion')}
          />
          <ItemContent>
            <ItemTitle>{t('electronicVersion')}</ItemTitle>
            <span>
              {t('createdOn', { date: createdDate ? dates.formatDate(createdDate) : '' })}
            </span>
            <ItemButton onClick={displayPdf}>{t('open')}</ItemButton>
          </ItemContent>
        </Item>

        {scenarioSettings?.transferDetails === IwScenarioSettingsDetails.WillInstructionAsset && (
          <Item>
            <ImageStyled src={assets} width={240} height={152} alt={t('assets')} />

            <ItemContent>
              <ItemTitle>{t('assets')}</ItemTitle>
              <span>{t('assetsAssigned')}</span>
              <ItemButton
                onClick={() => {
                  modals.closeModal();
                  router.push(routes.assets());
                }}
                icon={<Icon name="arrow-up-right" size="s20" />}
                iconPosition="end"
              >
                {t('view')}
              </ItemButton>
            </ItemContent>
          </Item>
        )}
      </InnerForm>
      <Buttons>
        <ButtonIw onClick={modals.closeModal}>{t('ok')}</ButtonIw>
      </Buttons>
    </ModalContainerV5>
  );
};

const InnerForm = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px;

  ${media.iwMobile} {
    flex-direction: column;
    gap: 24px;
    padding-top: 0;
    overflow: auto;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;

  ${media.iwMobile} {
    flex-direction: column;
    margin-top: auto;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.colorBorder};
  border-radius: 12px;
  max-width: 240px;

  ${media.iwMobile} {
    border: none;
    flex-direction: row;
    align-items: center;
    max-width: 100%;
    gap: 8px;
  }
`;

const ItemTitle = styled.b`
  font-size: 16px;
`;

const ItemContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;

  ${media.iwMobile} {
    padding: 0;
    height: fit-content;
  }
`;

const ItemButton = styled(ButtonIwTextStyled)`
  margin-top: auto;
  width: fit-content;

  ${media.iwMobile} {
    margin-top: 0;
  }
`;

const ImageStyled = styled(Image)`
  ${media.iwMobile} {
    width: 184px;
    height: 130px;
  }
`;
